import { Box, Button, Stack, TextField } from '@mui/material';
import { useState } from 'react';

import {
  useBoardEditingStore,
  useBoardViewportStore,
  useWorkspaceUiStore,
  type WallBoard,
} from '@walls/store';
import { useClusterCommands } from '../clusters/hooks/useClusterCommands';
import { CardActionPanel } from './CardActionPanel';
import { CardColorPicker } from './CardColorPicker';
import { useFocusTextFieldAtEnd } from './hooks/useFocusTextFieldAtEnd';
import { boardCardColors } from './utils/cardColors';

interface AddClusterDialogProps {
  board: WallBoard;
}

export function AddClusterDialog({ board }: AddClusterDialogProps) {
  const titleField = useFocusTextFieldAtEnd([]);
  const clusterCommands = useClusterCommands(board);
  const stopEditing = useBoardEditingStore((state) => state.stopEditing);
  const clusterDraftRect = useBoardViewportStore((state) => state.clusterDraftRect);
  const dropPosition = useBoardViewportStore((state) => state.dropPosition);
  const setClusterDraftRect = useBoardViewportStore((state) => state.setClusterDraftRect);
  const setDropPosition = useBoardViewportStore((state) => state.setDropPosition);
  const selectedCardColor = useWorkspaceUiStore((state) => state.selectedCardColor);
  const setSelectedCardColor = useWorkspaceUiStore((state) => state.setSelectedCardColor);

  const [showColorPicker, setShowColorPicker] = useState(false);
  const [color, setColor] = useState(selectedCardColor);
  const [title, setTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const trimmedTitle = title.trim();

  const close = () => {
    if (!isSaving) {
      setClusterDraftRect(null);
      setDropPosition(null);
      stopEditing();
    }
  };

  const save = async () => {
    if (!dropPosition || !trimmedTitle || isSaving) {
      return;
    }

    setIsSaving(true);

    try {
      await clusterCommands.createClusterAt(dropPosition, trimmedTitle, color, clusterDraftRect);
      setClusterDraftRect(null);
      setDropPosition(null);
      stopEditing();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CardActionPanel backgroundColor={color} onClose={close}>
      <TextField
        autoFocus
        label="Group Title"
        inputRef={titleField.inputRef}
        multiline
        minRows={1}
        value={title}
        onFocus={titleField.onFocus}
        onChange={(event) => setTitle(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            void save();
          }
        }}
      />
      <Stack direction="row" justifyContent="flex-end" alignItems="end" spacing={2}>
        <Box sx={{ position: 'relative' }} onClick={(event) => event.stopPropagation()}>
          <Box
            component="button"
            type="button"
            aria-label={`Use color ${color}`}
            onClick={() => setShowColorPicker(!showColorPicker)}
            sx={{
              bgcolor: color,
              border: '1px solid #d0d5dd',
              borderRadius: '50%',
              boxShadow: color === '#ffffff' ? 'inset 0 0 0 1px #e4e7ec' : undefined,
              cursor: 'pointer',
              height: 30,
              width: 30,
              p: 0,
            }}
          />
          {showColorPicker && (
            <CardColorPicker
              colors={boardCardColors}
              value={color}
              onChange={(nextColor) => {
                setShowColorPicker(false);
                setColor(nextColor);
                setSelectedCardColor(nextColor);
              }}
            />
          )}
        </Box>
        <Button
          variant="contained"
          onClick={() => void save()}
          disabled={!trimmedTitle || isSaving}
        >
          Create
        </Button>
      </Stack>
    </CardActionPanel>
  );
}
